import React, {useEffect, useState} from 'react';
import {
  CurrencyDollarIcon,
  DocumentTextIcon,
  UserIcon,
  LightningBoltIcon,
  CloudUploadIcon,
  ChatAlt2Icon,
  ChevronDownIcon,
  ArrowRightIcon,
} from '@heroicons/react/outline';
import {useAppContext, useAnalytics} from '../../hooks';
import PlanCard from '../../components/PlanCard';
import DocumentHeader from '../../components/DocumentHeader';
import Button, {ButtonSizes, ButtonTypes} from '../../components/ButtonNew';
import TestimonialCard from '../../components/TestimonialCard';
import {resumeReviewFeatures, resumeReviewCallFeatures} from '../../constants/product';
import {COMPANY_DATA} from '../../constants/companies';

import Nick from '../../../assets/images/testimonials/nick.jpeg';
import Jake from '../../../assets/images/testimonials/jake.jpg';
import {getBrandName} from '../../components/Brand';

const features = [
  {
    name: 'Expert senior tech reviewers',
    description: 'Get feedback from experienced hiring managers and recruiters who have reviewed thousands of applications at top tech companies. Learn what actually matters.',
    icon: UserIcon,
  },
  {
    name: 'Personalized, actionable feedback',
    description: 'Forget generic advice. We help you craft the perfect resume with specific, actionable improvements based on your target role and experiences.',
    icon: DocumentTextIcon,
  },
  {
    name: 'Money-back guarantee',
    description: "We're not satisfied until you are. Work with us until you get the resume you want. If you're not satisfied, get your money back.",
    icon: CurrencyDollarIcon,
  },
];

const howItWorks = [
  {
    step: '01',
    name: 'Upload your resume',
    description: 'Share your current resume and tell us about the roles you are targeting. Takes less than 2 minutes.',
    icon: CloudUploadIcon,
  },
  {
    step: '02',
    name: 'Expert review',
    description: 'A senior recruiter or hiring manager from a top tech company reviews your resume line-by-line.',
    icon: ChatAlt2Icon,
  },
  {
    step: '03',
    name: 'Get actionable feedback',
    description: 'Receive specific edits, rewrites, and a prioritized list of changes that will get you more interviews.',
    icon: LightningBoltIcon,
  },
];

const testimonials = [
  {
    name: 'Nick Culpin',
    title: 'Software Engineer',
    image: Nick.src,
    text: 'This framework was exactly the kind of clarity I was looking for, in particular the job skills and experience section helped me to improve how I articulate data analysis and trim the fat on my resume.',
  },
  {
    name: 'Jake',
    title: 'Software Engineer',
    image: Jake.src,
    text: 'Thanks for the feedback on my resume; this is the exact kind of candid shake-up feedback I was hoping to receive.',
  },
];

const ResumeFAQs = [
  {
    question: "What's included in my resume review?",
    answer: 'For the live 45-minute video call, your reviewer will walk you through specific recommendations and suggestions to improve your resume. If you opt for review over email instead, all this feedback will be sent to you in a personalized document. Our resume experts will explain how to edit the language, format, and details of your resume to maximize the chances of your application.',
  },
  {
    question: 'How soon will I receive my resume review?',
    answer: "Our team starts reviewing your resume right away. For the 45-minute video call, you'll receive feedback during your scheduled session. For email reviews, your review will be sent as soon as it's ready. We guarantee completion within 5 business days, but in most cases, you can expect to receive it within 48 hours or less.",
  },
  {
    question: "What's your refund policy?",
    answer: "We're confident that you'll find our resume review helpful and informative. If you aren't satisfied with your review for any reason, contact us for a re-review or get a full refund.",
  },
  {
    question: "Can I get my resume reviewed again if I've already submitted it once?",
    answer: 'Each resume review includes reviewing one version of your resume, but you are welcome to purchase a follow-up review for feedback on your updated resume.',
  },
  {
    question: `Can ${getBrandName()} review my cover letter or LinkedIn profile?`,
    answer: "We don't offer these services yet, but if you're interested in these services, feel free to reach out to support@aced.io.",
  },
];

const reviewerCompanyNames = ['Meta', 'Google', 'Microsoft', 'Amazon', 'OpenAI', 'Airbnb', 'Stripe', 'TikTok'];

function FaqRow({question, answer}) {
  const [open, setOpen] = useState(false);
  const contentId = `faq-answer-${question.replace(/\s+/g, '-').toLowerCase()}`;
  return (
    <div className="border-b border-gray-200">
      <button
        type="button"
        className="w-full flex justify-between items-center py-5 text-left focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={contentId}
      >
        <span className="text-base md:text-lg font-medium text-gray-900">{question}</span>
        <ChevronDownIcon
          className={`h-5 w-5 text-gray-500 transform transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>
      <div
        id={contentId}
        hidden={!open}
        className="pb-5 pr-8 text-base text-gray-500"
      >
        {answer}
      </div>
    </div>
  );
}

function SkeletonLine({width = 'w-full', color = 'bg-gray-200'}) {
  return <div className={`h-2 rounded-sm ${color} ${width}`} />;
}

function CommentBubble({initials, initialsBg, children}) {
  return (
    <div className="flex items-start gap-2 rounded-lg bg-white border border-gray-200 shadow-lg px-2.5 py-2 w-[170px]">
      <div className={`h-6 w-6 shrink-0 rounded-full ${initialsBg} text-white text-[10px] font-bold flex items-center justify-center`}>
        {initials}
      </div>
      <div className="min-w-0">
        <div className="text-[10px] font-bold text-gray-900 leading-tight">Reviewer</div>
        <div className="text-[10px] text-gray-600 leading-snug mt-0.5">{children}</div>
      </div>
    </div>
  );
}

function ResumeMockup() {
  return (
    <div className="relative bg-white rounded-t-2xl shadow-2xl border border-gray-200 border-b-0 w-full max-w-md mx-auto">
      {/* Window chrome */}
      <div className="flex items-center gap-1.5 border-b border-gray-200 px-4 py-2.5 rounded-t-2xl bg-gray-50">
        <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-yellow-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-green-400" />
      </div>
      <div className="px-7 pt-7 pb-10">
        {/* Header */}
        <div className="flex items-baseline justify-between border-b border-gray-200 pb-4">
          <div>
            <div className="text-lg font-extrabold text-gray-900 tracking-tight">Jane Smith</div>
            <div className="mt-1 text-[11px] text-gray-500 font-medium">Senior Software Engineer · San Francisco, CA</div>
          </div>
          <div className="text-[10px] text-gray-400 font-medium">jane@email.com</div>
        </div>

        {/* Experience section */}
        <div className="mt-5">
          <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Experience</div>
          <div className="mt-3">
            <div className="flex items-baseline justify-between">
              <div className="text-xs font-bold text-gray-900">Software Engineer, Acme Corp</div>
              <div className="text-[10px] text-gray-400 font-medium">2021 — Present</div>
            </div>
            <ul className="mt-3 space-y-2.5">
              <li className="flex gap-2 items-start">
                <span className="text-gray-300 shrink-0 text-[10px] leading-none pt-1">•</span>
                <div className="flex-1 space-y-1.5 pt-0.5">
                  <SkeletonLine width="w-full" />
                  <SkeletonLine width="w-10/12" />
                </div>
              </li>
              <li className="relative flex gap-2 items-start">
                <span className="text-gray-300 shrink-0 text-[10px] leading-none pt-1">•</span>
                <div className="relative flex-1 bg-emerald-100/70 rounded px-1.5 py-1.5 space-y-1.5">
                  <SkeletonLine width="w-full" color="bg-emerald-300/60" />
                  <SkeletonLine width="w-9/12" color="bg-emerald-300/60" />
                </div>
                {/* Floating comment — offset right and up, well inside hero overflow */}
                <div className="absolute -top-6 right-2 hidden lg:block z-10">
                  <CommentBubble initials="SR" initialsBg="bg-emerald-500">
                    Strong impact — great use of metrics.
                  </CommentBubble>
                </div>
              </li>
              <li className="flex gap-2 items-start">
                <span className="text-gray-300 shrink-0 text-[10px] leading-none pt-1">•</span>
                <div className="flex-1 space-y-1.5 pt-0.5">
                  <SkeletonLine width="w-11/12" />
                </div>
              </li>
              <li className="relative flex gap-2 items-start">
                <span className="text-gray-300 shrink-0 text-[10px] leading-none pt-1">•</span>
                <div className="relative flex-1 bg-amber-100/60 rounded px-1.5 py-1.5 space-y-1.5">
                  <SkeletonLine width="w-11/12" color="bg-amber-300/50" />
                </div>
                {/* Floating comment — different horizontal offset to stagger from the green one */}
                <div className="absolute -bottom-8 right-12 hidden lg:block z-10">
                  <CommentBubble initials="SR" initialsBg="bg-amber-500">
                    Add a metric to quantify the impact.
                  </CommentBubble>
                </div>
              </li>
            </ul>
          </div>

          {/* Second experience block — skeleton only */}
          <div className="mt-10">
            <div className="flex items-baseline justify-between">
              <div className="text-xs font-bold text-gray-900">Software Engineer, Initech</div>
              <div className="text-[10px] text-gray-400 font-medium">2019 — 2021</div>
            </div>
            <ul className="mt-3 space-y-2.5">
              <li className="flex gap-2 items-start">
                <span className="text-gray-300 shrink-0 text-[10px] leading-none pt-1">•</span>
                <div className="flex-1 space-y-1.5 pt-0.5">
                  <SkeletonLine width="w-11/12" />
                  <SkeletonLine width="w-8/12" />
                </div>
              </li>
              <li className="flex gap-2 items-start">
                <span className="text-gray-300 shrink-0 text-[10px] leading-none pt-1">•</span>
                <div className="flex-1 space-y-1.5 pt-0.5">
                  <SkeletonLine width="w-10/12" />
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Education section */}
        <div className="mt-7">
          <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Education</div>
          <div className="mt-3 flex items-baseline justify-between">
            <div className="text-xs font-bold text-gray-900">B.S. Computer Science, Stanford University</div>
            <div className="text-[10px] text-gray-400 font-medium">2017 — 2021</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CompanyLogoStrip() {
  return (
    <div className="relative max-w-6xl mx-auto px-4 pt-12 pb-2">
      <p className="text-center text-sm font-semibold text-gray-500 mb-6">
        Our reviewers have hired at
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4">
        {reviewerCompanyNames.filter((n) => COMPANY_DATA[n]).map((name) => (
          <div key={name} className="col-span-1 flex justify-center items-center">
            <img
              alt={name}
              src={COMPANY_DATA[name]}
              className="h-10 w-24 object-contain grayscale opacity-50"
              style={{mixBlendMode: 'darken'}}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ResumeLandingPage({reviewers = []}) {
  const {tracker} = useAnalytics();
  const {catalog} = useAppContext();

  useEffect(() => {
    tracker.resumeViewLanding();
  }, []);

  return (
    <div className="relative bg-white overflow-hidden">
      <DocumentHeader
        title="Craft the Perfect Resume for More Interviews"
        socialDescription="Find out what your application is missing, stand out to recruiters, and land more interviews."
      />

      {/* Hero */}
      <section className="relative border-b border-gray-200 overflow-hidden">
        <main className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-10 sm:pt-20">
          <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-10 md:gap-16 items-center">
            <div className="pb-10 sm:pb-12 text-center lg:text-left">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.02] tracking-tight text-gray-900">
                Get more interviews with the perfect resume.
              </h1>
              <p className="mt-6 text-lg text-gray-600 leading-relaxed max-w-xl text-pretty mx-auto md:mx-0">
                Personalized, line-by-line feedback from senior recruiters and
                hiring managers at top tech companies.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:justify-center lg:justify-start">
                <Button
                  size={ButtonSizes.LARGE}
                  href="/resume/upload"
                  onClick={() => tracker.resumeClickUpload({button: 'top'})}
                  className="w-full sm:w-auto px-7 py-3.5 text-base md:text-lg md:px-8 md:py-4 rounded-lg inline-flex items-center justify-center gap-2"
                >
                  Upload resume
                  <ArrowRightIcon className="h-4 w-4" strokeWidth={2.5} />
                </Button>
                <Button
                  size={ButtonSizes.LARGE}
                  buttonType={ButtonTypes.SECONDARY}
                  href="#pricing"
                  className="w-full sm:w-auto px-7 py-3.5 text-base md:text-lg md:px-8 md:py-4 rounded-lg justify-center"
                >
                  See pricing
                </Button>
              </div>
            </div>

            <div className="hidden lg:block self-end -mb-px">
              <ResumeMockup />
            </div>
          </div>
        </main>
      </section>

      {/* Company logo strip */}
      <CompanyLogoStrip />

      {/* How it works */}
      <section className="py-20 max-w-6xl mx-auto px-4">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="text-sm font-bold uppercase tracking-wider text-indigo mb-2">How it works</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-gray-900">
            From upload to interviews in 3 steps
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {howItWorks.map((step, idx) => (
            <div
              key={step.name}
              className="relative bg-white border border-gray-200 rounded-2xl p-6"
            >
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center justify-center h-12 w-12 rounded-xl bg-indigo-100 text-indigo">
                  <step.icon className="h-6 w-6" aria-hidden="true" />
                </div>
                <span className="text-5xl font-extrabold text-gray-100">{step.step}</span>
              </div>
              <h3 className="text-lg font-bold text-gray-900">{step.name}</h3>
              <p className="mt-2 text-sm text-gray-600 leading-relaxed">{step.description}</p>
              {idx < howItWorks.length - 1 && (
                <div className="hidden md:block absolute top-12 -right-3 text-gray-300" aria-hidden="true">
                  →
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 md:px-8 bg-gray-50 border-y border-gray-200">
        <div className="mx-auto max-w-7xl">
          <h2 className="text-center text-3xl md:text-4xl font-extrabold text-gray-900 tracking-tight">
            Trusted by candidates
          </h2>
          <p className="text-center text-base text-gray-600 mt-3 max-w-xl mx-auto text-pretty">
            Here&apos;s what candidates say after a resume review with our experts.
          </p>
          <div className="mt-11 grid grid-cols-1 md:grid-cols-2 gap-5 max-w-5xl mx-auto">
            {testimonials.map((t) => (
              <TestimonialCard key={t.name} testimonial={t} truncateText={false} />
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20">
        <div className="text-center max-w-2xl mx-auto px-4 mb-12">
          <p className="text-sm font-bold uppercase tracking-wider text-indigo mb-2">Pricing</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-gray-900">
            Simple pricing. Guaranteed results.
          </h2>
          <p className="mt-4 text-base text-gray-600">
            Every review comes with a money-back guarantee. No subscriptions. No hidden fees.
          </p>
          {reviewers.length > 0 && (
            <div className="mt-6 flex items-center justify-center gap-3">
              <div className="flex -space-x-2">
                {reviewers.slice(0, 4).map((reviewer) => (
                  <img
                    key={reviewer.slug}
                    src={reviewer.photo}
                    alt={reviewer.name}
                    loading="lazy"
                    className="h-8 w-8 rounded-full object-cover ring-2 ring-white"
                  />
                ))}
              </div>
              <span className="text-sm font-semibold text-gray-700">
                Reviewed by a real human, never AI.
              </span>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-x-6 justify-center overflow-x-auto px-4">
          <PlanCard
            name="Review over email"
            description="Get detailed resume feedback from an expert"
            features={resumeReviewFeatures}
            price={catalog?.RESUME_REVIEW.priceString}
            priceSecondaryText="per review"
            cta="Upload resume →"
            href="/resume/upload"
            onClick={() => tracker.resumeClickUpload({button: 'emailPlanCard'})}
            className="mb-4"
          />
          <PlanCard
            name="45-min call"
            description="Chat 1-on-1 with an expert for real-time resume feedback"
            badge={{
              text: 'Popular',
              backgroundColor: '#D8F7FE',
              textColor: '#2174A3',
            }}
            highlighted
            features={resumeReviewCallFeatures}
            price={catalog?.RESUME_REVIEW_CALL.priceString}
            priceSecondaryText="per session"
            cta="Upload resume →"
            href="/resume/upload?from=call"
            onClick={() => tracker.resumeClickUpload({button: 'callPlanCard'})}
            className="mb-4"
          />
        </div>
      </section>

      {/* Meet your reviewers — real humans behind the review */}
      {reviewers.length > 0 && (
        <section className="py-20 max-w-6xl mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="text-sm font-bold uppercase tracking-wider text-indigo mb-2">Real human review</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-gray-900">
              A real recruiter reviews your resume — line by line.
            </h2>
            <p className="mt-4 text-base text-gray-600 text-pretty">
              No AI, no templates. Your resume is read by an actual senior recruiter or
              hiring manager from a top tech company — the same people who decide who
              lands the interview.
            </p>
          </div>

          <p className="mb-8 text-center text-xs font-semibold uppercase tracking-wider text-gray-400">
            Example recruiters from our team
          </p>

          <ul className="flex flex-wrap justify-center gap-x-8 gap-y-10">
            {reviewers.map((reviewer) => (
              <li key={reviewer.slug} className="flex w-32 flex-col items-center text-center">
                <img
                  src={reviewer.photo}
                  alt={reviewer.name}
                  loading="lazy"
                  className="h-24 w-24 rounded-full object-cover shadow-md ring-2 ring-white"
                />
                <div className="mt-3 text-sm font-bold text-gray-900">{reviewer.name}</div>
                {reviewer.title && (
                  <div className="mt-0.5 text-xs leading-snug text-gray-500">{reviewer.title}</div>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* FAQs */}
      <section className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl tracking-tight font-extrabold text-gray-900">
            Frequently asked
          </h2>
        </div>
        <div className="border-t border-gray-200">
          {ResumeFAQs.map((faq) => (
            <FaqRow key={faq.question} question={faq.question} answer={faq.answer} />
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-4 md:px-8 my-16">
        <div className="mx-auto max-w-7xl relative overflow-hidden rounded-3xl px-6 py-14 md:py-16 text-center text-white bg-[linear-gradient(135deg,var(--color-indigo-900),var(--color-indigo))]">
          <div
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_50%,rgba(255,255,255,0.1),transparent_50%)]"
            aria-hidden="true"
          />
          <div className="relative">
            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight">
              Ready to land your next interview?
            </h2>
            <p className="mt-4 text-lg opacity-90">
              Upload your resume and get expert feedback in as little as 48 hours.
            </p>
            <div className="mt-7 flex flex-wrap justify-center gap-3">
              {/* FIXME: Button inversion override */}
              <Button
                href="/resume/upload"
                onClick={() => tracker.resumeClickUpload({button: 'footerCta'})}
                className="!bg-white !text-indigo px-7 py-3.5 text-base md:text-lg md:px-8 md:py-4 rounded-lg inline-flex items-center gap-2"
              >
                Upload resume →
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
